import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Typeahead } from 'react-bootstrap-typeahead'
import URL from '../service/API_URL'
import './style.css'

const authConfig = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem('essential_attempt_token') || ''}` } })

const formatTime = (seconds) => {
  const safe = Math.max(0, seconds)
  return `${String(Math.floor(safe / 60)).padStart(2, '0')}:${String(safe % 60).padStart(2, '0')}`
}

const countWords = (value) => (value.trim().match(/[\p{L}\p{N}]+(?:['’-][\p{L}\p{N}]+)*/gu) || []).length

const EssentialTest = () => {
  const navigate = useNavigate()
  const attemptId = sessionStorage.getItem('essential_attempt_id')
  const [payload, setPayload] = useState(null)
  const [itemIndex, setItemIndex] = useState(0)
  const [saveState, setSaveState] = useState('Saved')
  const [error, setError] = useState('')
  const [remaining, setRemaining] = useState(0)
  const [audioSession, setAudioSession] = useState(null)
  const [audioPhase, setAudioPhase] = useState('idle')
  const [submitting, setSubmitting] = useState(false)
  const [showProfile, setShowProfile] = useState(false)
  const instructionAudio = useRef(null)
  const questionAudio = useRef(null)
  const pendingSaves = useRef(new Set())
  const integrityViolationSent = useRef(false)

  const loadAttempt = useCallback(async () => {
    if (!attemptId || !sessionStorage.getItem('essential_attempt_token')) {
      navigate('/', { replace: true })
      return
    }
    try {
      const response = await URL.essentialAPI.get(`attempts/${attemptId}`, authConfig())
      const data = response.data.data
      if (data.attempt.status === 'submitted') {
        navigate('/essential-placement-test/result', { replace: true })
        return
      }
      setPayload(data)
      setItemIndex(0)
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Your test could not be loaded. Check your connection and try again.')
    }
  }, [attemptId, navigate])

  useEffect(() => { loadAttempt() }, [loadAttempt])

  useEffect(() => {
    const blockClipboard = (event) => {
      event.preventDefault()
      setError('Copy, cut, paste, and drag-and-drop are disabled during the Essentials test.')
    }
    const blockClipboardShortcut = (event) => {
      if ((event.ctrlKey || event.metaKey) && ['c', 'v', 'x'].includes(event.key.toLowerCase())) {
        blockClipboard(event)
      }
    }
    const invalidateForTabChange = () => {
      if (document.visibilityState !== 'hidden' || integrityViolationSent.current || !attemptId) return
      integrityViolationSent.current = true
      const token = sessionStorage.getItem('essential_attempt_token') || ''
      const endpoint = `${URL.essentialAPI.defaults.baseURL}attempts/${attemptId}/integrity-violation`

      window.localStorage.setItem('essential_integrity_notice', 'Your previous Essentials test was cancelled because the test tab was left. Please register and restart from Listening.')
      sessionStorage.removeItem('essential_attempt_id')
      sessionStorage.removeItem('essential_attempt_token')
      sessionStorage.removeItem('essential_candidate')

      window.fetch(endpoint, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason: 'page_hidden' }),
      }).catch(() => {})
      window.location.replace('/')
    }

    document.addEventListener('copy', blockClipboard)
    document.addEventListener('cut', blockClipboard)
    document.addEventListener('paste', blockClipboard)
    document.addEventListener('contextmenu', blockClipboard)
    document.addEventListener('dragstart', blockClipboard)
    document.addEventListener('drop', blockClipboard)
    document.addEventListener('keydown', blockClipboardShortcut)
    document.addEventListener('visibilitychange', invalidateForTabChange)

    return () => {
      document.removeEventListener('copy', blockClipboard)
      document.removeEventListener('cut', blockClipboard)
      document.removeEventListener('paste', blockClipboard)
      document.removeEventListener('contextmenu', blockClipboard)
      document.removeEventListener('dragstart', blockClipboard)
      document.removeEventListener('drop', blockClipboard)
      document.removeEventListener('keydown', blockClipboardShortcut)
      document.removeEventListener('visibilitychange', invalidateForTabChange)
    }
  }, [attemptId])

  const section = payload?.attempt?.current_section
  const items = useMemo(() => {
    if (section === 'listening') return payload?.audio_groups || []
    if (section === 'reading') return payload?.passages || []
    return payload?.writing ? [payload.writing] : []
  }, [payload, section])
  const currentItem = items[itemIndex]
  const currentQuestions = useMemo(() => {
    if (!payload || !currentItem) return []
    if (section === 'writing') return []
    return payload.questions.filter((question) => section === 'listening'
      ? question.audio_group_id === currentItem.id
      : question.passage_id === currentItem.id)
  }, [payload, currentItem, section])
  const answeredCount = useMemo(() => section === 'writing'
    ? (payload?.writing?.word_count || 0)
    : (payload?.questions?.filter((question) => question.skill === section && question.answer && (question.answer.option_id || question.answer.text)).length || 0), [payload, section])
  const sectionQuestionCount = section === 'writing' ? (payload?.writing?.minimum_words || 250) : 30

  useEffect(() => {
    if (!payload) return undefined
    const deadline = section === 'listening'
      ? payload.attempt.listening_deadline
      : section === 'reading' ? payload.attempt.reading_deadline : payload.attempt.writing_deadline
    if (!deadline) return undefined
    const tick = () => {
      const seconds = Math.max(0, Math.floor((new Date(deadline).getTime() - Date.now()) / 1000))
      setRemaining(seconds)
      if (seconds === 0) {
        if (section === 'listening') beginReading(true)
        else if (section === 'reading') beginWriting(true)
        else submitFinal({})
      }
    }
    tick()
    const timer = window.setInterval(tick, 1000)
    return () => window.clearInterval(timer)
    // beginReading and submitFinal intentionally use the latest server state after the timer fires.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload?.attempt?.listening_deadline, payload?.attempt?.reading_deadline, payload?.attempt?.writing_deadline, section])

  useEffect(() => {
    setAudioSession(null)
    setAudioPhase(currentItem?.started_at ? 'used' : 'idle')
  }, [currentItem?.id, currentItem?.started_at])

  const saveAnswer = (questionId, patch) => {
    setSaveState('Saving…')
    setError('')
    setPayload((current) => ({
      ...current,
      questions: current.questions.map((question) => question.id === questionId
        ? { ...question, answer: { ...(question.answer || {}), ...patch } }
        : question),
    }))
    const request = URL.essentialAPI.put(`attempts/${attemptId}/answers`, { answers: [{ question_id: questionId, option_id: patch.option_id || null, text: patch.text ?? null }] }, authConfig())
      .then(() => setSaveState('Saved'))
      .catch((requestError) => {
        setSaveState('Not saved')
        setError(requestError.response?.data?.message || 'This answer could not be saved. Try again before continuing.')
      })
      .finally(() => pendingSaves.current.delete(request))
    pendingSaves.current.add(request)
    return request
  }

  const startAudio = async () => {
    if (!currentItem || audioPhase !== 'idle') return
    setAudioPhase('loading')
    setError('')
    try {
      const response = await URL.essentialAPI.post(`attempts/${attemptId}/audio-groups/${currentItem.id}/start`, {}, authConfig())
      setAudioSession(response.data.data)
      setAudioPhase('instruction')
      window.setTimeout(() => instructionAudio.current?.play(), 0)
    } catch (requestError) {
      setAudioPhase('used')
      setError(requestError.response?.data?.message || 'This audio cannot be started again.')
    }
  }

  const toggleAudio = () => {
    const audio = audioPhase === 'instruction' ? instructionAudio.current : questionAudio.current
    if (!audio) return
    if (audio.paused) audio.play()
    else audio.pause()
  }

  const beginQuestionAudio = () => {
    setAudioPhase('question')
    window.setTimeout(() => questionAudio.current?.play(), 0)
  }

  const beginReading = async (automatic = false) => {
    if (submitting) return
    setSubmitting(true)
    setError('')
    try {
      await Promise.all(Array.from(pendingSaves.current))
      await URL.essentialAPI.post(`attempts/${attemptId}/sections/reading/start`, {}, authConfig())
      await loadAttempt()
    } catch (requestError) {
      if (!automatic) setError(requestError.response?.data?.message || 'Reading could not be started. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const beginWriting = async (automatic = false) => {
    if (submitting) return
    setSubmitting(true)
    setError('')
    try {
      await Promise.all(Array.from(pendingSaves.current))
      await URL.essentialAPI.post(`attempts/${attemptId}/sections/writing/start`, {}, authConfig())
      await loadAttempt()
    } catch (requestError) {
      if (!automatic) setError(requestError.response?.data?.message || 'Writing could not be started. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const saveWriting = (response) => {
    const wordCount = countWords(response)
    setSaveState('Saving…')
    setPayload((current) => ({ ...current, writing: { ...current.writing, response, word_count: wordCount } }))
    const request = URL.essentialAPI.put(`attempts/${attemptId}/writing`, { response }, authConfig())
      .then(() => setSaveState('Saved'))
      .catch((requestError) => {
        setSaveState('Not saved')
        setError(requestError.response?.data?.message || 'Your Writing response could not be saved.')
      })
      .finally(() => pendingSaves.current.delete(request))
    pendingSaves.current.add(request)
    return request
  }

  const submitFinal = async (profile) => {
    if (submitting) return
    setSubmitting(true)
    setError('')
    try {
      await Promise.all(Array.from(pendingSaves.current))
      await URL.essentialAPI.post(`attempts/${attemptId}/submit`, profile, authConfig())
      navigate('/essential-placement-test/result', { replace: true })
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Your test could not be submitted. Your saved answers are still safe.')
    } finally {
      setSubmitting(false)
    }
  }

  if (!payload) {
    return <main className="essential-shell essential-loading"><p>{error || 'Preparing your test…'}</p>{error && <button className="essential-primary" onClick={loadAttempt}>Try again</button>}</main>
  }

  return (
    <main className="essential-test-page">
      <header className="essential-test-header">
        <div><span className="essential-kicker">ESSENTIALS</span><strong>{section ? section.charAt(0).toUpperCase() + section.slice(1) : ''}</strong></div>
        <div className="essential-header-progress"><span>{section === 'writing' ? `${answeredCount} words · ${sectionQuestionCount} target` : `${answeredCount}/${sectionQuestionCount} answered`}</span><div className="essential-progress"><span style={{ width: `${Math.min(100, (answeredCount / sectionQuestionCount) * 100)}%` }} /></div></div>
        <time aria-label="Time remaining">{formatTime(remaining)}</time>
      </header>

      <div className={`essential-layout ${section === 'writing' ? 'writing' : ''}`}>
        {section !== 'writing' && <aside className="essential-navigator" aria-label={`${section} navigation`}>
          {items.map((item, index) => {
            const questions = payload.questions.filter((question) => section === 'listening' ? question.audio_group_id === item.id : question.passage_id === item.id)
            const complete = questions.every((question) => question.answer && (question.answer.option_id || question.answer.text))
            return <button key={item.id} type="button" className={`${index === itemIndex ? 'active' : ''} ${complete ? 'complete' : ''}`} onClick={() => setItemIndex(index)}>{section === 'listening' ? item.group_number : item.section}</button>
          })}
        </aside>}

        <section className={`essential-question-card ${section === 'reading' ? 'reading-split-card' : ''}`}>
          {section === 'listening' ? (
            <>
              <div className="essential-card-heading"><div><span>Audio {currentItem.group_number} of {items.length}</span><h1>Questions {currentItem.start_question}-{currentItem.end_question}</h1></div><span className={`audio-status ${audioPhase}`}>{audioPhase === 'used' ? 'Played' : audioPhase === 'done' ? 'Completed' : 'One play'}</span></div>
              <div className="essential-audio-panel">
                {audioSession && <>
                  <audio ref={instructionAudio} src={audioSession.instruction_url} onEnded={beginQuestionAudio} preload="auto" />
                  <audio ref={questionAudio} src={audioSession.question_url} onEnded={() => setAudioPhase('done')} preload="auto" />
                </>}
                {audioPhase === 'idle' && <button className="essential-audio-start" type="button" onClick={startAudio}>▶ Play instruction and questions</button>}
                {audioPhase === 'loading' && <p>Loading audio…</p>}
                {(audioPhase === 'instruction' || audioPhase === 'question') && <button className="essential-audio-start" type="button" onClick={toggleAudio}>{audioPhase === 'instruction' ? 'Pause or resume instruction' : 'Pause or resume questions'}</button>}
                {(audioPhase === 'used' || audioPhase === 'done') && <p>{audioPhase === 'used' ? 'This audio was already started and cannot be replayed.' : 'Audio completed. Review your selected answers before continuing.'}</p>}
              </div>
              <div className="essential-question-list">
                {currentQuestions.map((question) => <EssentialQuestion key={question.id} question={question} onAnswer={saveAnswer} />)}
              </div>
            </>
          ) : section === 'reading' ? (
            <div className="essential-reading-split">
              <article className="essential-reading-pane passage-pane">
                <div className="essential-card-heading"><div><span>Passage {currentItem.section} of {items.length}</span><h1>{currentItem.title}</h1></div></div>
                <div className="essential-passage">{currentItem.body.split('\n').map((paragraph, index) => <p key={index}>{paragraph}</p>)}</div>
                <p className="essential-instruction">{currentItem.instructions}</p>
              </article>
              <section className="essential-reading-pane quiz-pane" aria-label={`Questions for ${currentItem.title}`}>
                <div className="reading-quiz-heading"><span>Questions</span><strong>{currentQuestions[0]?.number}-{currentQuestions[currentQuestions.length - 1]?.number}</strong></div>
                <div className="essential-question-list">
                  {currentQuestions.map((question) => <EssentialQuestion key={question.id} question={question} onAnswer={saveAnswer} />)}
                </div>
              </section>
            </div>
          ) : <EssentialWriting writing={payload.writing} onSave={saveWriting} />}
          {error && <p className="essential-error" role="alert">{error}</p>}
        </section>
      </div>

      <footer className="essential-bottom-bar">
        <span className={`essential-save-state ${saveState === 'Not saved' ? 'error' : ''}`} aria-live="polite">{saveState}</span>
        <div>
          <button className="essential-secondary" type="button" disabled={itemIndex === 0} onClick={() => setItemIndex((index) => index - 1)}>Previous</button>
          {section !== 'writing' && itemIndex < items.length - 1
            ? <button className="essential-primary" type="button" onClick={() => setItemIndex((index) => index + 1)}>Next</button>
            : section === 'listening'
              ? <button className="essential-primary" type="button" onClick={() => beginReading(false)} disabled={submitting}>{submitting ? 'Opening Reading…' : 'Finish Listening'}</button>
              : section === 'reading'
                ? <button className="essential-primary" type="button" onClick={() => beginWriting(false)} disabled={submitting}>{submitting ? 'Opening Writing…' : 'Finish Reading'}</button>
                : <button className="essential-primary" type="button" onClick={() => setShowProfile(true)}>Submit for review</button>}
        </div>
      </footer>
      {showProfile && <EssentialProfileModal onClose={() => setShowProfile(false)} onSubmit={submitFinal} submitting={submitting} />}
    </main>
  )
}

const EssentialWriting = ({ writing, onSave }) => {
  const [response, setResponse] = useState(writing?.response || '')
  const saveTimer = useRef(null)

  useEffect(() => {
    setResponse(writing?.response || '')
    return () => window.clearTimeout(saveTimer.current)
  }, [writing?.task_id, writing?.response])

  const changeResponse = (value) => {
    setResponse(value)
    window.clearTimeout(saveTimer.current)
    saveTimer.current = window.setTimeout(() => onSave(value), 700)
  }

  const flushResponse = () => {
    window.clearTimeout(saveTimer.current)
    onSave(response)
  }

  return <div className="essential-writing-workspace">
    <div className="essential-card-heading"><div><span>Writing Task 2</span><h1>{writing?.title}</h1></div><span className="writing-review-badge">Teacher review</span></div>
    <div className="essential-writing-grid">
      <aside className="essential-writing-prompt">
        {(writing?.prompt || '').split('\n').map((paragraph, index) => paragraph ? <p key={index}>{paragraph}</p> : <br key={index} />)}
        <div className="essential-instruction">{writing?.instructions}</div>
      </aside>
      <div className="essential-writing-answer">
        <div><label htmlFor="essential-writing-response">Your answer</label><span>{countWords(response)} words</span></div>
        <textarea id="essential-writing-response" value={response} onChange={(event) => changeResponse(event.target.value)} onBlur={flushResponse} maxLength={writing?.max_characters || 12000} rows={24} spellCheck placeholder="Start writing your essay here…" />
        <small>Your response saves automatically. It will be reviewed after you submit the complete test.</small>
      </div>
    </div>
  </div>
}

const EssentialQuestion = ({ question, onAnswer }) => {
  const [text, setText] = useState(question.answer?.text || '')
  const saveTimer = useRef(null)

  useEffect(() => {
    setText(question.answer?.text || '')
    return () => window.clearTimeout(saveTimer.current)
  }, [question.id, question.answer?.text])

  const changeText = (value) => {
    setText(value)
    window.clearTimeout(saveTimer.current)
    saveTimer.current = window.setTimeout(() => onAnswer(question.id, { text: value, option_id: null }), 500)
  }

  const flushText = () => {
    window.clearTimeout(saveTimer.current)
    onAnswer(question.id, { text, option_id: null })
  }

  const sortedOptions = [...(question.options || [])].sort((first, second) => String(first.key || '').localeCompare(String(second.key || ''), undefined, { numeric: true, sensitivity: 'base' }))

  return <fieldset className="essential-question">
    <legend><span>{question.number}</span>{question.prompt}</legend>
    {question.type === 'text'
      ? <input type="text" value={text} maxLength={100} autoComplete="off" placeholder="Type one word" onChange={(event) => changeText(event.target.value)} onBlur={flushText} />
      : <div className="essential-options">{sortedOptions.map((option) => <label key={option.id} className={question.answer?.option_id === option.id ? 'selected' : ''}><input type="radio" name={`question-${question.id}`} checked={question.answer?.option_id === option.id} onChange={() => onAnswer(question.id, { option_id: option.id, text: null })} /><span className="option-key">{option.key}</span><span>{option.text}</span></label>)}</div>}
  </fieldset>
}

const EssentialProfileModal = ({ onClose, onSubmit, submitting }) => {
  const [provinces, setProvinces] = useState([])
  const [levels, setLevels] = useState([])
  const [schools, setSchools] = useState([])
  const [provinceId, setProvinceId] = useState('')
  const [educationCode, setEducationCode] = useState('')
  const [selectedSchool, setSelectedSchool] = useState([])
  const [error, setError] = useState('')

  useEffect(() => {
    Promise.all([URL.urlApplication.get('master/provinces'), URL.urlApplication.get('master/jenjang')])
      .then(([provinceResponse, levelResponse]) => { setProvinces(provinceResponse.data.data || []); setLevels(levelResponse.data.data || []) })
      .catch(() => setError('School data could not be loaded. Please try again.'))
  }, [])

  useEffect(() => {
    if (!provinceId || !educationCode) { setSchools([]); setSelectedSchool([]); return }
    URL.urlApplication.get(`sekolah-indonesia?province_id=${provinceId}&jenjang=${educationCode}`)
      .then((response) => setSchools(response.data.data || []))
      .catch(() => setError('The school list could not be loaded.'))
  }, [provinceId, educationCode])

  const finish = (event) => {
    event.preventDefault()
    if (!selectedSchool.length) { setError('Choose your school before submitting.'); return }
    onSubmit({ kode_sekolah: selectedSchool[0].kode_sekolah })
  }

  return <div className="profile-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="essential-profile-title"><form className="profile-modal" onSubmit={finish}><div className="profile-modal-head"><div><span className="section-kicker">Final step</span><h2 id="essential-profile-title">Complete your school profile</h2><p>We use this to identify your submission and prepare the NIEC review.</p></div><button className="modal-close" type="button" aria-label="Close" onClick={onClose}>×</button></div><div className="profile-fields"><div className="field-group"><label htmlFor="essential-province">School province</label><select id="essential-province" value={provinceId} onChange={(event) => setProvinceId(event.target.value)}><option value="">Choose province</option>{provinces.map((province) => <option key={province.id} value={province.id}>{province.province_name}</option>)}</select></div><div className="field-group"><label htmlFor="essential-level">Education level</label><select id="essential-level" value={educationCode} onChange={(event) => setEducationCode(event.target.value)}><option value="">Choose level</option>{levels.map((level) => <option key={level.code_jenjang} value={level.code_jenjang}>{level.jenjang_pendidikan}</option>)}</select></div><div className="field-group school-field"><label htmlFor="essential-school">School name</label><Typeahead id="essential-school" labelKey="nama_sekolah" options={schools} selected={selectedSchool} onChange={setSelectedSchool} disabled={!provinceId || !educationCode} placeholder="Search your school" /></div></div>{error && <p className="essential-error">{error}</p>}<div className="profile-actions"><button className="profile-back" type="button" onClick={onClose}>Review writing</button><button className="profile-finish" type="submit" disabled={submitting}>{submitting ? 'Submitting…' : 'Submit for NIEC review'}</button></div></form></div>
}

export default EssentialTest
